/**
 * Application keys & permissions overlay (Sprint 365 Stream B; CHG-GW-001).
 *
 * The applications page only lists applications; keys and their permissions are
 * managed here. Uses the shared responsive Dialog (centered >= 640px, bottom
 * sheet below, ADR-114) which restores focus to the trigger on close.
 */

import { useMemo } from 'react';
import { Ban, KeyRound, Plus, RefreshCw, ShieldCheck, ShieldOff } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { useI18n } from '@/i18n';
import { formatDateTime, KeyStatusChip } from './KeyStatusChip';
import { useGatewayKeys } from '../hooks/useGateway';
import type { GatewayApplication, GatewayKey } from '../types';

export interface ApplicationKeysDialogProps {
  application: GatewayApplication | null;
  open: boolean;
  onClose: () => void;
  canOperate: boolean;
  canAdmin: boolean;
  onCreateKey: (application: GatewayApplication) => void;
  onRotate: (key: GatewayKey) => void;
  onSetStatus: (key: GatewayKey, action: 'suspend' | 'resume') => void;
  onRevoke: (key: GatewayKey) => void;
}

export function ApplicationKeysDialog({
  application,
  open,
  onClose,
  canOperate,
  canAdmin,
  onCreateKey,
  onRotate,
  onSetStatus,
  onRevoke,
}: ApplicationKeysDialogProps) {
  const { t } = useI18n();
  const keysQuery = useGatewayKeys(application?.id ?? null);
  const keys = useMemo(() => keysQuery.data?.data ?? [], [keysQuery.data]);

  const statusLabel = (status: string) =>
    (['active', 'suspended', 'revoked', 'expired'] as const).includes(
      status as 'active' | 'suspended' | 'revoked' | 'expired',
    )
      ? t(`gateway.status.${status}`)
      : status;

  const permissions = useMemo(() => {
    const byScope = new Map<string, string[]>();
    for (const key of keys) {
      for (const scope of key.scopes) {
        const names = byScope.get(scope) ?? [];
        names.push(key.name);
        byScope.set(scope, names);
      }
    }
    return [...byScope.entries()]
      .map(([scope, names]) => ({ scope, keys: [...new Set(names)] }))
      .sort((a, b) => a.scope.localeCompare(b.scope));
  }, [keys]);

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent
        size="xl"
        ariaLabel={t('gateway.overlay.title', { name: application?.name ?? '' })}
        data-testid="application-keys-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
            {t('gateway.overlay.title', { name: application?.name ?? '' })}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="keys">
          <TabsList>
            <TabsTrigger value="keys" data-testid="overlay-tab-keys">
              {t('gateway.overlay.tabs.keys')}
            </TabsTrigger>
            <TabsTrigger value="permissions" data-testid="overlay-tab-permissions">
              {t('gateway.overlay.tabs.permissions')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="keys" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {t('gateway.overlay.keys.description')}
              </p>
              {canAdmin && application && (
                <Button
                  onClick={() => onCreateKey(application)}
                  data-testid="open-create-key"
                >
                  <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
                  {t('gateway.keys.create')}
                </Button>
              )}
            </div>
            {keysQuery.isLoading ? (
              <Spinner />
            ) : keysQuery.isError ? (
              <div
                className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
                role="alert"
                data-testid="keys-error"
              >
                <p>{t('gateway.keys.error')}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => void keysQuery.refetch()}
                  data-testid="keys-retry"
                >
                  {t('gateway.keys.retry')}
                </Button>
              </div>
            ) : keys.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="no-keys">
                {t('gateway.keys.empty')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="keys-table">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400">
                      <th className="py-2">{t('gateway.keys.columns.name')}</th>
                      <th className="py-2">{t('gateway.keys.columns.key')}</th>
                      <th className="py-2">{t('gateway.keys.columns.scopes')}</th>
                      <th className="py-2">{t('gateway.keys.columns.status')}</th>
                      <th className="py-2">{t('gateway.keys.columns.lastUsed')}</th>
                      <th className="py-2 text-right">{t('gateway.keys.columns.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map((key) => (
                      <tr key={key.id} className="border-t border-gray-100 dark:border-gray-700">
                        <td className="py-2 font-medium">{key.name}</td>
                        <td className="py-2 font-mono text-xs">
                          {key.key_prefix}…{key.key_last4}
                        </td>
                        <td className="py-2">
                          <div className="flex flex-wrap gap-1">
                            {key.scopes.map((scope) => (
                              <span
                                key={scope}
                                className="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-700"
                              >
                                {scope}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="py-2">
                          <KeyStatusChip status={key.status} label={statusLabel(key.status)} />
                        </td>
                        <td className="py-2">{formatDateTime(key.last_used_at)}</td>
                        <td className="py-2">
                          <div className="flex justify-end gap-2">
                            {canOperate && key.status === 'active' && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => onRotate(key)}
                                  data-testid={`rotate-${key.id}`}
                                >
                                  <RefreshCw className="h-3 w-3 mr-1" aria-hidden="true" />
                                  {t('gateway.keys.actions.rotate')}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => onSetStatus(key, 'suspend')}
                                  data-testid={`suspend-${key.id}`}
                                >
                                  <ShieldOff className="h-3 w-3 mr-1" aria-hidden="true" />
                                  {t('gateway.keys.actions.suspend')}
                                </Button>
                              </>
                            )}
                            {canOperate && key.status === 'suspended' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onSetStatus(key, 'resume')}
                                data-testid={`resume-${key.id}`}
                              >
                                <ShieldCheck className="h-3 w-3 mr-1" aria-hidden="true" />
                                {t('gateway.keys.actions.resume')}
                              </Button>
                            )}
                            {canAdmin && key.status !== 'revoked' && (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => onRevoke(key)}
                                data-testid={`revoke-${key.id}`}
                              >
                                <Ban className="h-3 w-3 mr-1" aria-hidden="true" />
                                {t('gateway.keys.actions.revoke')}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="permissions" className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t('gateway.overlay.permissions.description')}
            </p>
            {permissions.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="no-permissions">
                {t('gateway.overlay.permissions.empty')}
              </p>
            ) : (
              <table className="w-full text-sm" data-testid="permissions-table">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400">
                    <th className="py-2">{t('gateway.overlay.permissions.scope')}</th>
                    <th className="py-2">{t('gateway.overlay.permissions.grantedBy')}</th>
                  </tr>
                </thead>
                <tbody>
                  {permissions.map((entry) => (
                    <tr
                      key={entry.scope}
                      className="border-t border-gray-100 dark:border-gray-700"
                    >
                      <td className="py-2 font-mono text-xs">
                        <span className="inline-flex items-center gap-1">
                          <KeyRound className="h-3 w-3 text-primary" aria-hidden="true" />
                          {entry.scope}
                        </span>
                      </td>
                      <td className="py-2">{entry.keys.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
